import { all, call, put, select, takeEvery } from 'redux-saga/effects';
import ActionTypes from '../../../../Data/ActionTypes';
import MatchingSupplierCountSource from '../../../../Data/MatchingSupplierCountSource';
import { MATCHING_SUBCATEGORY_WHITELIST } from '../../../../Data/MatchingSuppliers';
import {
  convertPartialSubmissionToInputAnswers,
  isSupplierMatch,
} from '../../../../Helpers/supplierMatches';
import { getPartialSubmission } from '../../../../Helpers/submission';
import { setMatchingSupplierCountRequestScheduled } from '../../slices/supplierMatches';
import getMatchingSuppliersQuery from './getMatchingSuppliersQuery';

const getSupplierMatchComponents = (components) =>
  Object.values(components).filter((component) => isSupplierMatch(component));

const shouldSkipSubscriptionRequest = ({
  subcategoryName,
  supplierMatches,
  component,
}) => {
  if (!component?.component?.mappedQuestionId) return true;
  if (!MATCHING_SUBCATEGORY_WHITELIST.includes(subcategoryName)) return true;

  return (
    supplierMatches.matchingSupplierCountSource ===
      MatchingSupplierCountSource.MATCHES ||
    supplierMatches.matchingSupplierCountSource ===
      MatchingSupplierCountSource.FALLBACK
  );
};

export function* refreshMatchingSupplierCountWorker(action) {
  const { componentId } = action.payload;
  const { components, supplierMatches, formConfig, features } = yield select();
  const { showBrandMatchCount } = features || {};

  if (!showBrandMatchCount) {
    return;
  }

  const component = components[`component_${componentId}`];
  if (
    shouldSkipSubscriptionRequest({
      subcategoryName: formConfig.subcategoryName,
      supplierMatches,
      component,
    })
  ) {
    return;
  }

  const supplierMatchComponents = getSupplierMatchComponents(components);
  const answers = convertPartialSubmissionToInputAnswers(
    getPartialSubmission(components),
    supplierMatchComponents,
    supplierMatches.subcategoryMatches
  );

  if (answers.length === 0) return;

  let didDispatchLoading = false;
  try {
    yield put({
      type: 'SUPPLIER_MATCHES/requestMatchingSupplierCountLoading',
      payload: {
        isLoading: true,
        matchingSupplierCountLoadingSource: MatchingSupplierCountSource.PREVIEW,
      },
    });
    didDispatchLoading = true;

    const { matchingSupplierCount } = yield call(getMatchingSuppliersQuery, {
      variables: {
        subcategoryId: formConfig.subcategoryId,
        answers,
      },
    });

    if (
      supplierMatches.matchingSupplierCountSource ===
        MatchingSupplierCountSource.PREVIEW &&
      supplierMatches.matchingSupplierCount === matchingSupplierCount
    ) {
      return;
    }

    yield put({
      type: 'SUPPLIER_MATCHES/setMatchingSupplierCount',
      payload: {
        matchingSupplierCount,
        matchingSupplierCountSource: MatchingSupplierCountSource.PREVIEW,
      },
    });
  } catch (error) {
    // Silently swallow errors for now; rely on customer matches saga to provide fallbacks.
  } finally {
    if (didDispatchLoading) {
      yield put({
        type: 'SUPPLIER_MATCHES/requestMatchingSupplierCountLoading',
        payload: {
          isLoading: false,
          matchingSupplierCountLoadingSource: null,
        },
      });
    }
  }
}

export function* handleForwardNavigationWorker(action) {
  if (!action?.payload?.shouldNavigateForward) {
    return;
  }

  const { supplierMatches, features, formConfig } = yield select();
  const { showBrandMatchCount } = features || {};

  if (!showBrandMatchCount) {
    return;
  }

  const {
    matchingSupplierCountRequestScheduled,
    matchingSupplierCountRequestComponentId,
    matchingSupplierCountSource,
  } = supplierMatches || {};

  if (!matchingSupplierCountRequestScheduled) {
    return;
  }

  const hasFinalSource =
    matchingSupplierCountSource === MatchingSupplierCountSource.MATCHES ||
    matchingSupplierCountSource === MatchingSupplierCountSource.FALLBACK;

  if (
    !matchingSupplierCountRequestComponentId ||
    hasFinalSource ||
    !MATCHING_SUBCATEGORY_WHITELIST.includes(formConfig?.subcategoryName)
  ) {
    yield put(setMatchingSupplierCountRequestScheduled({ isScheduled: false }));
    return;
  }

  yield put({
    type: ActionTypes.REFRESH_MATCHING_SUPPLIER_COUNT,
    payload: { componentId: matchingSupplierCountRequestComponentId },
  });

  yield put(setMatchingSupplierCountRequestScheduled({ isScheduled: false }));
}

export default function* matchingSuppliersWatcher() {
  yield all([
    takeEvery(
      ActionTypes.REFRESH_MATCHING_SUPPLIER_COUNT,
      refreshMatchingSupplierCountWorker
    ),
    takeEvery(
      'NAVIGATION_CONTROLS/setShouldNavigateForward',
      handleForwardNavigationWorker
    ),
  ]);
}
